/* eslint-disable */
import React, { useMemo } from 'react';
import { StyledEngineProvider } from '@mui/material/styles';
import { ThemeProvider, Theme, createTheme } from '@mui/material/styles';
import {
  CssBaseline,
  IconButton,
  tableCellClasses,
  ThemeOptions
} from '@mui/material';
import '@mui/styles';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import '@mui/x-data-grid/themeAugmentation';

import palette from './palette';
import shadows from './shadows';
import typography from './typography';
import breakpointsX from './breakpoints';
import GlobalStyles from './globalStyles';
import borderRadius from './borderRadius';

declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line no-empty-interface
  interface DefaultTheme extends Theme {}
}

declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line no-empty-interface

  interface DefaultTheme extends Theme {}
}

declare module '@mui/material' {
  interface TypographyVariants {
    body3: React.CSSProperties;
    body4: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body3: true;
    body4: true;
    caption2: true;
    caption3: true;
    caption4: true;
    caption5: true;
    caption6: true;
  }
}

type Props = {
  children: React.ReactNode;
};

const ThemeContext = React.createContext<{
  theme: string;
}>(null);

function ThemeConfig({ children }: Props) {
  const isLight = true;
  const themeMode = 'retail3';
  const paletteTheme = palette[themeMode];
  const retail3ComponentThemeOptions: ThemeOptions['components'] = {
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px'
        }
      }
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: {
          color: 'unset'
        }
      }
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundImage: paletteTheme.gradients.primary,
          backgroundColor: 'rgba(9, 30, 66, 0.54)',
          inset: '0px'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          boxShadow: 'none'
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          boxSizing: 'border-box',
          background: '#fff',
          color: 'rgba(0, 0, 0, 0.87)',
          ...typography.retail3.body4,
          '&:hover': {
            borderColor: `1px solid ${palette.retail3.primary[900]} !important`
          }
        },
        notchedOutline: {
          border: '1px solid rgba(0, 0, 0, 0.12)',
          borderRadius: '8px'
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: '20px',
          fontStyle: 'normal'
        }
      }
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: paletteTheme.primary[50]
        },
        bar: {
          backgroundColor: 'rgba(48, 140, 191, 1)'
        }
      }
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent', //(paletteTheme.primary as any)[25],
          color: 'rgba(96, 107, 138, 1)'
        }
      }
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          border: '1px solid rgba(0, 0, 0, 0.08)',
          borderTop: 'none'
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          border: '1px solid rgba(0, 0, 0, 0.08)',
          borderBottom: 'none',
          boxSizing: 'content-box',
          '&:last-child': {
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
          }
        },
        head: {
          border: '1px solid transparent',
          backgroundColor: 'rgba(238, 246, 250, 1)'
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '5px 16px 5px 16px',
          color: palette.retail3.text.primary,
          ...typography.retail3.body4,
          [`&.${tableCellClasses.head}`]: {
            backgroundColor: paletteTheme.primary[25],
            color: paletteTheme.grey[500],
            ...(typography?.retail3?.caption || {}),
            padding: '16px 16px 8px 16px',
            verticalAlign: 'bottom'
          }
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFEBEE',
          border: '1px solid #EF9AA8'
        },
        icon: {
          color: paletteTheme.error[600]
        },
        message: {
          color: paletteTheme.text.secondary,
          ...(typography?.retail3?.body4 || {})
        }
      }
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: {
          color: 'rgba(0, 0, 0, 0.87)',
          ...(typography?.retail3?.body1 || {})
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          padding: 0
        },
        inputRoot: {
          display: 'flex',
          alignItems: 'center'
        },
        popupIndicator: {},
        option: {
          fontWeight: 400,
          fontSize: 14,
          backgroundColor: 'initial',
          textAlign: 'left'
        }
      },
      defaultProps: {
        popupIcon: (
          <IconButton size="small">
            <KeyboardArrowDown fontSize={'medium'} />
          </IconButton>
        )
      }
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: 'none',
          borderRadius: 0,
          '& .MuiDataGrid-main': {
            borderBottom: `1px solid ${paletteTheme.grey['50']}`
          },
          '& .MuiDataGrid-virtualScroller': {
            marginTop: '44px !important'
          },
          '& .MuiDataGrid-columnSeparator': {
            display: 'none'
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: paletteTheme.primary.summit,
            border: 'none',
            borderRadius: 0,
            minHeight: '44px !important',
            maxHeight: '44px !important',
            '& .MuiDataGrid-iconButtonContainer, .MuiButtonBase-root': {
              backgroundColor: paletteTheme.primary.summit
            }
          },
          '& .MuiDataGrid-columnHeader': {
            minHeight: '44px !important',
            maxHeight: '44px !important',
            padding: 8,
            lineHeight: 16
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            ...typography.retail3.caption,
            color: '#111827'
          },
          '& .MuiDataGrid-cell': {
            ...typography.retail3.body4,
            outline: 'none !important',
            padding: 8
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: paletteTheme.primary[25]
          },
          '& .MuiDataGrid-cell:focus': {
            outline: 'none'
          },
          '& .MuiDataGrid-columnHeader:focus': {
            outline: 'none'
          },
          '& .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none'
          },
          '& .MuiDataGrid-menuIcon': {
            display: 'none'
          },
          '& .MuiTablePagination-root': {
            '& .MuiTablePagination-selectLabel, & .MuiSelect-select, & .MuiTablePagination-displayedRows':
              {
                ...typography.retail3.caption2,
                color: paletteTheme.text.secondary
              },
            '& .MuiInputBase-root': {
              marginTop: 4
            }
          },
          '& .MuiDataGrid-footerContainer': {
            border: 'none'
          }
        }
      }
    }
  };

  const themeOptions: ThemeOptions = {
    palette: palette.retail3,
    shadows: shadows[isLight ? 'light' : 'dark'] as any,
    typography: typography['retail3'],
    shape: borderRadius,
    breakpoints: breakpointsX,
    components: retail3ComponentThemeOptions
  };

  const theme = useMemo(() => createTheme(themeOptions), [themeOptions]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <StyledEngineProvider injectFirst>
          <CssBaseline />
          <GlobalStyles />
          <ThemeContext.Provider value={{ theme: themeMode }}>
            {children}
          </ThemeContext.Provider>
        </StyledEngineProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

export default ThemeConfig;
